import { useState } from 'react';
import { useStore } from '../store.js';
import { INDUSTRIES } from './industries.js';

// Values must match SF prod picklist Entity_Type__c exactly.
// Must stay a SUPERSET of the Lead/Account Entity_Type__c picklist — a prefilled
// value this list lacks renders BLANK. 'Sole Proprietorship' is normalized to
// 'Sole Proprietor' in ApplyAppController.normalizeEntityType. Added 2026-08-28:
// 'S-Corp', 'Non-Profit' (active in SF, previously unrenderable).
const ENTITY_TYPES = ['Sole Proprietor', 'LLC', 'Corporation', 'S-Corp', 'Partnership', 'LLP', 'Ltd. Partnership', 'Non-Profit'];

// State abbreviations used for text fields (Business_Address_State__c — plain string, 'upper' transform).
const STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC','PR'];

// Full state names for State_of_Incorporation__c (restricted picklist).
const INCORP_STATES = [
  'Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut',
  'Delaware','Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa',
  'Kansas','Kentucky','Louisiana','Maine','Maryland','Massachusetts','Michigan',
  'Minnesota','Mississippi','Missouri','Montana','Nebraska','Nevada',
  'New Hampshire','New Jersey','New Mexico','New York','North Carolina',
  'North Dakota','Ohio','Oklahoma','Oregon','Pennsylvania','Rhode Island',
  'South Carolina','South Dakota','Tennessee','Texas','Utah','Vermont',
  'Virginia','Washington','West Virginia','Wisconsin','Wyoming','Washington DC',
];

const REQUIRED = [
  ['business_name',            'Business name'],
  ['federal_tax_id',           'Federal Tax ID'],
  ['business_start_date',      'Business start date'],
  ['industry',                 'Industry'],
  ['entity_type',              'Entity type'],
  ['business_address_street',  'Street address'],
  ['business_city',            'City'],
  ['business_state',           'State'],
  ['business_zip',             'ZIP'],
];

function validateBusiness(data) {
  const errors = {};
  for (const [k, label] of REQUIRED) {
    if (!String(data[k] ?? '').trim()) errors[k] = `${label} is required.`;
  }
  if (!errors.federal_tax_id) {
    const ein = (data.federal_tax_id || '').replace(/\D/g, '');
    if (ein.length !== 9) errors.federal_tax_id = 'Must be 9 digits.';
  }
  if (!errors.business_start_date) {
    // Browsers accept 5-digit years in type=date ("32010-01-01") — SF rejects them.
    const s = String(data.business_start_date || '');
    const y = Number(s.slice(0, 4));
    if (!/^\d{4}-\d{2}-\d{2}$/.test(s) || isNaN(new Date(s).getTime())) errors.business_start_date = 'Enter a valid date (4-digit year).';
    else if (y < 1900) errors.business_start_date = 'Date looks incorrect.';
    else if (new Date(s) > new Date()) errors.business_start_date = 'Start date can’t be in the future.';
  }
  if (!errors.business_zip) {
    const zip = (data.business_zip || '').replace(/\D/g, '').slice(0, 5);
    if (!/^\d{5}$/.test(zip)) errors.business_zip = 'Must be 5 digits.';
  }
  return errors;
}

function Field({ k, label, type = 'text', required, errors, clearError, ...rest }) {
  const value = useStore((s) => s.data[k] ?? '');
  const setField = useStore((s) => s.setField);
  const error = errors?.[k];
  return (
    <div>
      <label htmlFor={`f-${k}`} className="field-label">
        {label}{required && <span className="text-red-500"> *</span>}
      </label>
      <input
        id={`f-${k}`}
        type={type}
        className={`field-input ${error ? 'is-error' : ''}`}
        aria-invalid={!!error}
        min={rest.min}
        max={rest.max}
        value={value}
        onChange={(e) => {
          setField(k, e.target.value);
          if (error) clearError?.(k);
        }}
      />
      {error && <p className="field-error">{error}</p>}
    </div>
  );
}

function Select({ k, label, options, required, errors, clearError }) {
  const value = useStore((s) => s.data[k] ?? '');
  const setField = useStore((s) => s.setField);
  const error = errors?.[k];
  return (
    <div>
      <label htmlFor={`f-${k}`} className="field-label">
        {label}{required && <span className="text-red-500"> *</span>}
      </label>
      <select
        id={`f-${k}`}
        className={`field-select ${error ? 'is-error' : ''}`}
        aria-invalid={!!error}
        value={value}
        onChange={(e) => {
          setField(k, e.target.value);
          if (error) clearError?.(k);
        }}
      >
        <option value="">— select —</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
      {error && <p className="field-error">{error}</p>}
    </div>
  );
}

export function BusinessInfo({ onNext }) {
  const data = useStore((s) => s.data);
  const [errors, setErrors] = useState({});
  const clearError = (k) => setErrors((e) => { const { [k]: _, ...rest } = e; return rest; });

  function handleContinue() {
    const errs = validateBusiness(data);
    setErrors(errs);
    if (Object.keys(errs).length === 0) onNext();
  }

  const props = { errors, clearError };

  return (
    <div className="space-y-6">
      <header>
        <h2 className="text-xl font-semibold text-brand-900">Business information</h2>
        <p className="text-sm text-slate-500">Tell us about your business.</p>
      </header>

      <div className="grid sm:grid-cols-2 gap-4">
        <Field k="business_name" label="Business name" required {...props} />
        <Field k="dba" label="Doing business as" {...props} />
        <Field k="federal_tax_id" label="Federal Tax ID (EIN)" required {...props} />
        <Field k="business_start_date" label="Business start date" type="date" required min="1900-01-01" max={new Date().toISOString().slice(0, 10)} {...props} />
        <Select k="industry" label="Industry" options={INDUSTRIES} required {...props} />
        <Select k="entity_type" label="Legal entity type" options={ENTITY_TYPES} required {...props} />
        <Select k="state_of_incorporation" label="State of incorporation" options={INCORP_STATES} {...props} />
      </div>

      <div>
        <h3 className="font-medium text-slate-800 mb-2">Business address</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field k="business_address_street" label="Street" required {...props} />
          <Field k="business_address_line2" label="Suite / unit" {...props} />
          <Field k="business_city" label="City" required {...props} />
          <Select k="business_state" label="State" options={STATES} required {...props} />
          <Field k="business_zip" label="ZIP" required {...props} />
        </div>
      </div>

      <div>
        <h3 className="font-medium text-slate-800 mb-2">Landlord / mortgage</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field k="landlord_name" label="Landlord or mortgage holder name" {...props} />
          <Field k="landlord_amount" label="Monthly amount" {...props} />
        </div>
      </div>

      <div className="flex justify-end">
        <button className="btn-primary" onClick={handleContinue}>Continue</button>
      </div>
    </div>
  );
}
